import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { TotpSetupResponse } from '@shared/types';
import { useAuthStore } from '../app/authStore';
import { ApiError, api, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, CardHeader, CopyField, PageHeader, Skeleton } from '../components/ui';
import { Field, Input, Notice, PasswordInput } from '../components/form';
import { Modal, useToast } from '../components/feedback';
import { QRCode } from '../components/QRCode';

/**
 * 安全设置。
 *
 * 两步验证的分三步是刻意的：**拿到密钥 → 用验证码证明配对成功 → 才真正生效**。
 * 如果一扫完就置为启用，用户扫失败就会被锁在自己账号外面。
 */
export function SecurityPage() {
  const session = useAuthStore((state) => state.session);
  const refresh = useAuthStore((state) => state.refresh);
  const toast = useToast();

  const status = useAsync<{ totpEnabled: boolean; unusedRecoveryCodes: number }>(
    () => api.get('/api/auth/totp/status'),
    [],
  );

  // 改密
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwErrors, setPwErrors] = useState<Record<string, string>>({});

  // 两步验证
  const [setup, setSetup] = useState<TotpSetupResponse | null>(null);
  const [setupCode, setSetupCode] = useState('');
  const [setupBusy, setSetupBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenPassword, setRegenPassword] = useState('');
  const [totpBusy, setTotpBusy] = useState(false);

  const totpEnabled = status.data?.totpEnabled ?? session?.user.totpEnabled ?? false;

  async function changePassword() {
    setPwErrors({});
    if (next !== confirmPw) {
      setPwErrors({ newPassword: '两次输入的新口令不一致' });
      return;
    }
    setPwBusy(true);
    try {
      await useAuthStore.getState().changePassword(current, next);
      setCurrent('');
      setNext('');
      setConfirmPw('');
      toast.push('口令已更新，其它设备上的登录已失效', 'ok');
    } catch (error) {
      if (error instanceof ApiError) {
        setPwErrors(error.fieldErrors);
        if (Object.keys(error.fieldErrors).length === 0) toast.push(error.message, 'danger');
      } else {
        toast.push(describeError(error), 'danger');
      }
    } finally {
      setPwBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader title="安全设置" description="口令、两步验证与恢复码。这些设置跟着账号走，不随组织变化。" />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* ------------------------------------------------ 修改口令 */}
        <Card>
          <CardHeader title="修改口令" hint="至少 10 位，含字母与数字" />
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void changePassword();
            }}
          >
            <Field label="当前口令" htmlFor="pw-current" error={pwErrors.currentPassword}>
              <PasswordInput id="pw-current" value={current} onChange={setCurrent} autoComplete="current-password" required />
            </Field>
            <Field
              label="新口令"
              htmlFor="pw-next"
              error={pwErrors.newPassword}
              hint="不能与当前口令或邮箱相同"
            >
              <PasswordInput id="pw-next" value={next} onChange={setNext} autoComplete="new-password" required />
            </Field>
            <Field label="确认新口令" htmlFor="pw-confirm">
              <PasswordInput id="pw-confirm" value={confirmPw} onChange={setConfirmPw} autoComplete="new-password" required />
            </Field>
            <Notice tone="info">改密后，其它设备上的登录会立即失效，当前设备保持登录。</Notice>
            <Button type="submit" variant="primary" loading={pwBusy}>
              更新口令
            </Button>
          </form>
        </Card>

        {/* ---------------------------------------------- 两步验证 */}
        <Card>
          <CardHeader
            title="两步验证"
            hint="基于 TOTP，与 Google Authenticator / 1Password / 微软验证器兼容"
            action={totpEnabled ? <Badge tone="ok">已开启</Badge> : <Badge tone="warn">未开启</Badge>}
          />

          {status.loading ? (
            <Skeleton className="h-32 w-full" />
          ) : totpEnabled ? (
            <div className="space-y-3">
              <Notice tone="ok">
                账号已开启两步验证。登录时需要额外输入验证器中的 6 位数字。
              </Notice>
              <p className="text-[13px] text-fg-muted">
                剩余可用恢复码：
                <span className="tabular ml-1 font-medium text-fg">{status.data?.unusedRecoveryCodes ?? 0}</span> 个
                {status.data && status.data.unusedRecoveryCodes <= 2 ? ' —— 建议立即重新生成一批' : ''}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setRegenOpen(true)}>重新生成恢复码</Button>
                <Button variant="danger" onClick={() => setDisableOpen(true)}>
                  关闭两步验证
                </Button>
              </div>
            </div>
          ) : setup ? (
            <div className="space-y-4">
              <p className="text-[13px] text-fg-muted">
                用验证器 App 扫描下面的二维码；扫不了就手动输入密钥。
              </p>
              <div className="flex justify-center">
                <QRCode value={setup.otpauthUrl} size={192} />
              </div>
              <CopyField label="密钥（手动输入用）" value={setup.secret} hint="密钥只显示这一次，请勿截图外发。" />
              <Field label="验证码" htmlFor="totp-code" hint="填入验证器当前显示的 6 位数字以完成绑定">
                <Input
                  id="totp-code"
                  value={setupCode}
                  onChange={(value) => setSetupCode(value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                />
              </Field>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  loading={setupBusy}
                  onClick={async () => {
                    setSetupBusy(true);
                    try {
                      const result = await api.post<{ recoveryCodes: string[] }>('/api/auth/totp/enable', {
                        code: setupCode,
                      });
                      setRecoveryCodes(result.recoveryCodes);
                      setSetup(null);
                      setSetupCode('');
                      await status.reload();
                      await refresh();
                      toast.push('两步验证已开启', 'ok');
                    } catch (error) {
                      toast.push(
                        error instanceof ApiError && error.code === 'invalid_totp'
                          ? '验证码不正确，请确认手机时间是否准确'
                          : describeError(error),
                        'danger',
                      );
                    } finally {
                      setSetupBusy(false);
                    }
                  }}
                >
                  启用
                </Button>
                <Button onClick={() => setSetup(null)} disabled={setupBusy}>
                  取消
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <Notice tone="warn">
                两步验证能挡住口令泄漏带来的绝大部分风险。开启后请同时保存好恢复码。
              </Notice>
              <Button
                variant="primary"
                onClick={async () => {
                  try {
                    const result = await api.post<TotpSetupResponse>('/api/auth/totp/setup');
                    setSetup(result);
                  } catch (error) {
                    toast.push(describeError(error), 'danger');
                  }
                }}
              >
                开始绑定验证器
              </Button>
            </div>
          )}
        </Card>
      </div>

      <Card>
        <CardHeader title="会话与登录历史" hint="查看当前登录的设备，或结束可疑会话" />
        <div className="flex flex-wrap gap-3 text-[13px]">
          <Link to="/security/sessions" className="text-brand hover:underline">
            会话与设备
          </Link>
          <Link to="/security/history" className="text-brand hover:underline">
            登录历史
          </Link>
        </div>
      </Card>

      {/* --------------------------------------------------- 恢复码弹窗 */}
      <Modal
        open={recoveryCodes !== null}
        title="恢复码（只显示这一次）"
        description="验证器丢失时用它登录。每枚只能使用一次，请离线保存。"
        size="md"
        onClose={() => setRecoveryCodes(null)}
        footer={
          <Button variant="primary" onClick={() => setRecoveryCodes(null)}>
            我已妥善保存
          </Button>
        }
      >
        <Notice tone="warn">离开本窗口后将无法再次查看这些恢复码。</Notice>
        <ul className="mt-4 grid grid-cols-2 gap-2 font-mono text-[13px]">
          {recoveryCodes?.map((recoveryCode) => (
            <li key={recoveryCode} className="rounded-control bg-nest px-3 py-2 text-fg">
              {recoveryCode}
            </li>
          ))}
        </ul>
      </Modal>

      {/* --------------------------------------------------- 关闭确认 */}
      <Modal
        open={disableOpen}
        title="关闭两步验证"
        description="关闭后，账号只靠口令保护。"
        onClose={() => setDisableOpen(false)}
        footer={
          <>
            <Button onClick={() => setDisableOpen(false)} disabled={totpBusy}>
              取消
            </Button>
            <Button
              variant="danger"
              loading={totpBusy}
              onClick={async () => {
                setTotpBusy(true);
                try {
                  await api.post('/api/auth/totp/disable', { password: disablePassword, code: disableCode });
                  setDisableOpen(false);
                  setDisablePassword('');
                  setDisableCode('');
                  await status.reload();
                  await refresh();
                  toast.push('两步验证已关闭', 'ok');
                } catch (error) {
                  toast.push(describeError(error), 'danger');
                } finally {
                  setTotpBusy(false);
                }
              }}
            >
              确认关闭
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="当前口令" htmlFor="disable-password" required>
            <PasswordInput id="disable-password" value={disablePassword} onChange={setDisablePassword} />
          </Field>
          <Field label="验证码" htmlFor="disable-code" required>
            <Input
              id="disable-code"
              value={disableCode}
              onChange={(value) => setDisableCode(value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
            />
          </Field>
        </div>
      </Modal>

      {/* ------------------------------------- 重新生成恢复码（需要口令） */}
      <Modal
        open={regenOpen}
        title="重新生成恢复码"
        description="旧的一批会立即作废，生成后请重新保存。"
        onClose={() => setRegenOpen(false)}
        footer={
          <>
            <Button onClick={() => setRegenOpen(false)} disabled={totpBusy}>
              取消
            </Button>
            <Button
              variant="primary"
              loading={totpBusy}
              onClick={async () => {
                setTotpBusy(true);
                try {
                  const result = await api.post<{ recoveryCodes: string[] }>(
                    '/api/auth/totp/recovery-codes',
                    { password: regenPassword },
                  );
                  setRecoveryCodes(result.recoveryCodes);
                  setRegenOpen(false);
                  setRegenPassword('');
                  await status.reload();
                } catch (error) {
                  toast.push(describeError(error), 'danger');
                } finally {
                  setTotpBusy(false);
                }
              }}
            >
              生成
            </Button>
          </>
        }
      >
        <Field label="当前口令" htmlFor="regen-password" required hint="重新生成恢复码属于敏感操作，需要再次确认身份。">
          <PasswordInput id="regen-password" value={regenPassword} onChange={setRegenPassword} autoComplete="current-password" />
        </Field>
      </Modal>
    </div>
  );
}
